<script setup>
import Avatar from 'primevue/avatar'
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import Popover from 'primevue/popover'
import Tag from 'primevue/tag'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

const props = defineProps({
  size: {
    type: String,
    default: '2.5rem',
  },
  image: {
    type: String,
    default: null,
  },
})

const { t } = useI18n()
const authStore = useAuthStore()
const router = useRouter()

const user = computed(() => authStore.userData)

const avatarImage = computed(() => props.image || user.value?.photo || undefined)

const avatarStyles = computed(() => ({
  width: props.size,
  height: props.size,
  fontSize: `calc(${props.size} * 0.4)`,
}))

const initials = computed(() => {
  if (!user.value)
    return ''

  const first = user.value?.firstName?.charAt(0) ?? ''
  const last = user.value?.lastName?.charAt(0) ?? ''

  return `${first}${last}`.toUpperCase()
})

const menu = ref()
const isPopoverOpen = ref(false)

function displayContextMenu(event) {
  menu.value.toggle(event)
}

function handlePopoverShow() {
  isPopoverOpen.value = true
}

function handlePopoverHide() {
  isPopoverOpen.value = false
}

function handleReleaseNotes() {
  const { href } = router.resolve({ name: 'ReleaseNotes' })
  window.open(href, '_blank')
  menu.value?.hide()
}

async function handleLogout() {
  try {
    await authStore.logout()
    router.push({ name: 'AuthLogin' })
  }
  catch (error) {
    console.error('Logout failed:', error)
  }
}
</script>

<template>
  <Avatar
    class="text-sm cursor-pointer rounded-full hover:filter hover:brightness-90 transition-all duration-200 ease-in-out"
    :class="[
      avatarImage ? 'bg-white' : 'bg-[#F3F4F6] text-dss-surface-925 font-medium',
      { 'filter brightness-90': isPopoverOpen },
    ]"
    shape="circle"
    :image="avatarImage"
    :label="!avatarImage ? initials : undefined"
    :style="avatarStyles"
    @click="displayContextMenu($event)"
  />

  <Popover
    id="avatar-popover"
    ref="menu"
    class="w-56 bg-white dark:bg-dss-surface-50 before:hidden after:hidden !mt-2 !rounded-2xl !border border-dss-surface-200 shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A] font-inter"
    :pt="{ content: { class: 'p-2 flex flex-col max-h-[calc(100vh-5rem)] overflow-hidden' } }"
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <div class="flex flex-col gap-4 flex-1 min-h-0">
      <div class="flex flex-col gap-4 pt-2 px-3 flex-1 min-h-0">
        <span class="block text-xs font-medium uppercase text-dss-surface-850 shrink-0">{{ t('core.menu.user.profile') }}</span>

        <div class="flex flex-col gap-2 flex-1 min-h-0">
          <span class="text-dss-surface-925 text-sm font-medium shrink-0">
            {{ user?.firstName }} {{ user?.lastName }}
          </span>
          <span class="text-dss-surface-600 text-xs shrink-0">
            {{ user?.email }}
          </span>

          <div
            v-if="user?.permissionGroups?.length"
            class="flex flex-wrap gap-2 overflow-y-auto pr-1 content-start"
          >
            <Tag
              v-for="role in user?.permissionGroups"
              :key="role._id"
              :value="role.name"
              class="bg-dss-surface-200! border! border-dss-surface-300! rounded-lg! text-xs! px-2! py-1! !text-dss-surface-925"
            />
          </div>
        </div>
      </div>

      <Divider class="my-0! -mx-3! w-auto! shrink-0" />

      <div class="flex flex-col shrink-0">
        <Button
          icon="pi pi-align-justify"
          class="py-[10px]! px-3! text-sm font-medium! !text-dss-surface-925 bg-white! dark:!bg-dss-surface-50 border-0 focus:outline-none focus:ring-0 text-left! gap-1! hover:filter hover:brightness-90 transition-all duration-200 ease-in-out"
          :label="t('core.header.tooltips.release-notes')"
          data-testid="avatar-release-notes"
          @click="handleReleaseNotes"
        />

        <Button
          icon="pi pi-sign-out"
          class="py-[10px]! px-3! text-sm font-medium! !text-dss-surface-925 bg-white! dark:!bg-dss-surface-50 border-0 focus:outline-none focus:ring-0 text-left! gap-1! hover:filter hover:brightness-90 transition-all duration-200 ease-in-out"
          :label="t('core.menu.user.logout')"
          data-testid="avatar-logout"
          @click="handleLogout"
        />
      </div>
    </div>
  </Popover>
</template>
